/**
 * Polls the learner's notifications and raises an OS notification for each new
 * one, while running inside the desktop shell.
 *
 * Polling rather than a push channel because Platform has no notification
 * stream and adding one is a backend change this work does not make. Sixty
 * seconds is slow enough to be invisible in load terms and fast enough that a
 * nudge is not stale by the time it lands, and a window focus refetches
 * immediately so coming back to the app never shows an out-of-date badge.
 *
 * The GraphQL query is used rather than the REST notifications serializer
 * because only GraphQL returns `title` and `message`, and an OS notification
 * with neither is an empty grey rectangle.
 */

import { gql, useQuery } from "@apollo/client";
import { useCallback, useEffect, useRef } from "react";

import type { WoneDesktopBridge } from "./bridge";
import { DEFAULT_DEEP_LINK_ROUTE } from "./deep-link";
import {
  allIds,
  loadSeenIds,
  saveSeenIds,
  selectUnnotified,
  toDesktopNotification,
  type LearnerNotification,
} from "./notifications";

export const POLL_INTERVAL_MS = 60_000;

/**
 * The previous record plus this poll's ids, de-duplicated and oldest first.
 * Written as an array rather than built through a Set spread because the web
 * app compiles to ES5, where spreading a Set is a compile error.
 */
function mergeSeen(seen: Set<string> | null, ids: readonly string[]): string[] {
  const merged: string[] = seen === null ? [] : Array.from(seen);
  const known = new Set(merged);
  ids.forEach((id) => {
    if (known.has(id)) return;
    known.add(id);
    merged.push(id);
  });
  return merged;
}

export const MY_NOTIFICATIONS = gql`
  query DesktopMyNotifications {
    myNotifications {
      id
      title
      message
      dismissed
    }
  }
`;

interface QueryData {
  myNotifications?: (LearnerNotification | null)[] | null;
}

/**
 * Raises an OS notification for every notification the learner has not been
 * told about yet.
 *
 * Inert unless both conditions hold: the desktop bridge exists and the learner
 * is authenticated. In a browser the query never runs at all, so this hook
 * costs a mounted component and nothing else.
 *
 * The first run after an install is deliberately silent. `loadSeenIds` returns
 * `null` when there is no record, `selectUnnotified` turns that into an empty
 * list, and the ids are written straight through. Without that, installing the
 * app would fire a toast for every unread notification the learner has ever
 * accumulated.
 */
export function useNotificationPoller(
  bridge: WoneDesktopBridge | null,
  authenticated: boolean
): void {
  const enabled = bridge !== null && authenticated;

  const { data, refetch } = useQuery<QueryData>(MY_NOTIFICATIONS, {
    skip: !enabled,
    pollInterval: enabled ? POLL_INTERVAL_MS : 0,
    // The cache would answer the poll from memory and the effect below would
    // never see a new row.
    fetchPolicy: "network-only",
    // A failed poll is not worth a screen-level error: the next one is sixty
    // seconds away and nothing in the UI depends on this data.
    onError: () => undefined,
  });

  const onFocus = useCallback(() => {
    if (!enabled) return;
    void refetch().catch(() => undefined);
  }, [enabled, refetch]);

  useEffect(() => {
    if (!enabled) return undefined;
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [enabled, onFocus]);

  // Guards against a re-render with the same data re-notifying: the seen set
  // is only persisted after a successful pass, and a ref keeps the in-flight
  // pass from overlapping itself.
  const notifying = useRef(false);

  useEffect(() => {
    if (bridge === null || !enabled) return;
    const rows = (data?.myNotifications ?? []).filter(
      (n): n is LearnerNotification => n !== null && typeof n?.id === "string"
    );
    if (rows.length === 0) return;
    if (notifying.current) return;
    notifying.current = true;

    const seen = loadSeenIds();
    const fresh = selectUnnotified(rows, seen);

    // Written before the notifications are dispatched, not after. A crash
    // between the two would otherwise repeat every toast on the next launch,
    // and a missed notification is a much smaller failure than a duplicated
    // one that keeps coming back.
    saveSeenIds(mergeSeen(seen, allIds(rows)));

    void Promise.all(
      fresh.map((row) =>
        bridge.notify(toDesktopNotification(row, DEFAULT_DEEP_LINK_ROUTE)).catch(() => undefined)
      )
    ).finally(() => {
      notifying.current = false;
    });
  }, [bridge, enabled, data]);
}
